import { useCallback, useEffect, useState } from 'react';
import { PageTitle } from '@/components';
import { SlidingTabs } from '@/components/ui/SlidingTabs';
import { useUpdateBlogPost } from '@/services/mutations/blog';
import { useGetBlogPost } from '@/services/queries/blog';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BlogPostContent } from '@/pages/public/blog/components/BlogPostContent';
import { BlogPostForm } from './components/BlogPostForm';

interface PreviewData {
	title: string;
	content: string;
	banner_url: string;
	created_at?: string;
}

const EditBlogPost = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [searchParams, setSearchParams] = useSearchParams();

	const onBack = () => navigate('/admin/blog');

	const { data } = useGetBlogPost(id as string);
	const { mutate, isPending } = useUpdateBlogPost(onBack);

	const [previewData, setPreviewData] = useState<PreviewData | null>(null);

	useEffect(() => {
		if (data && !previewData) {
			setPreviewData({
				title: data.title,
				content: data.content,
				banner_url: data.banner_url,
				created_at: data.created_at,
			});
		}
	}, [data, previewData]);

	const handleValuesChange = useCallback((values: PreviewData) => {
		setPreviewData(values);
	}, []);

	const onSubmit = (body: Blog.UpdateBlogPost) =>
		mutate({ id: id as string, body });

	const defaultActiveTab =
		searchParams.get('tab') === 'preview' ? 'preview' : 'edicao';

	const tabs = [
		{
			id: 'edicao',
			label: 'Edição',
			children: data ? (
				<BlogPostForm
					formTitle="Editar post"
					onBack={onBack}
					onSubmit={onSubmit}
					isSubmitting={isPending}
					defaultValues={data}
					submitButtonText="Salvar alterações"
					onValuesChange={handleValuesChange}
				/>
			) : null,
		},
		{
			id: 'preview',
			label: 'Preview',
			children: previewData ? (
				<div className="overflow-hidden rounded-xl border border-zinc-100 bg-white">
					<div className="border-b border-zinc-100 bg-zinc-50 px-4 py-2 text-xs text-zinc-400">
						Pré-visualização — reflete os valores atuais do
						formulário
					</div>
					<BlogPostContent
						title={previewData.title}
						content={previewData.content}
						banner_url={previewData.banner_url}
						created_at={previewData.created_at}
						isPreview
					/>
				</div>
			) : null,
		},
	];

	return (
		<section className="space-y-4 px-4 py-8">
			<PageTitle text="Blog" />
			{data && (
				<SlidingTabs
					tabs={tabs}
					defaultActiveTab={defaultActiveTab}
					onChange={(tabId) => {
						const params = new URLSearchParams(searchParams);
						if (tabId === 'preview') {
							params.set('tab', 'preview');
						} else {
							params.delete('tab');
						}
						setSearchParams(params, { replace: true });
					}}
				/>
			)}
		</section>
	);
};

export { EditBlogPost };
